import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { smoothScrollTo } from "@/lib/smoothScroll";
import cetanavHero from "@/assets/cetanav-hero-tugboat.jpg.asset.json";

import cetanavFleet from "@/assets/cetanav-fleet.jpg.asset.json";

const CetaNav = () => {
  const isMobile = useIsMobile();
  const anim = (delay = 0) =>
    isMobile
      ? {
          initial: false as const,
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0 },
        }
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: "-80px" },
          transition: { duration: 0.6, delay },
        };

  const features = [
    {
      title: "Fleet overview",
      description: "See all your vessels on one map in real time.",
    },
    {
      title: "Fuel estimation",
      description: "GPS-based fuel consumption estimates without requiring a flow meter.",
    },
    {
      title: "Cloud dashboard",
      description:
        "Shore-based access to trip detection, route and zone alerts, fleet benchmarking, reports and trip logs.",
    },
    {
      title: "Captain display",
      description: "Onboard access to key operational data.",
    },
  ];

  const flow = [
    { title: "Navigation signals", description: "Position, speed and movement from GPS" },
    { title: "Cetasol Main Unit", description: "Data gathered onboard by the CMU" },
    { title: "Cloud", description: "Secure transfer to the Cetasol cloud" },
    { title: "Onboard + shore", description: "Visualised for crew and shore teams" },
  ];

  const visibility = [
    "Real-time vessel location",
    "Trip detection",
    "Route monitoring",
    "Zone alerts",
    "Unexpected vessel movement",
    "Fleet benchmarking",
    "Fuel consumption estimation",
    "Trip logs and reporting",
  ];

  const questions = [
    "Where are the vessels right now?",
    "Are they operating when they should?",
    "Are routes changing?",
    "Are vessels idling unexpectedly?",
    "How much fuel are they likely consuming?",
  ];

  const growth = [
    { name: "CetaNav", sub: "Vessel oversight", to: "/cetanav" },
    { name: "Digital bridge", sub: "Digitalising the bridge", to: "/ihelm-intelligent-platform" },
    { name: "CetaFuel", sub: "Accurate fuel reading", to: "/cetafuel" },
  ];

  return (
    <div className="min-h-screen section-fade">
      <Helmet>
        <html lang="en" />
        <title>CetaNav — Fleet Tracking and Vessel Monitoring | Cetasol</title>
        <meta
          name="description"
          content="CetaNav gives fleet managers vessel tracking, route monitoring and fuel consumption estimation using navigation data, with no engine sensors required."
        />
        <link rel="canonical" href="https://cetasol.com/cetanav" />
        <meta property="og:title" content="CetaNav — Fleet Tracking and Vessel Monitoring | Cetasol" />
        <meta
          property="og:description"
          content="Fleet visibility and operational insights without engine sensors. Track vessels, monitor routes and estimate fuel consumption with CetaNav."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cetasol.com/cetanav" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CetaNav — Fleet Tracking and Vessel Monitoring | Cetasol" />
        <meta
          name="twitter:description"
          content="Fleet visibility and operational insights without engine sensors."
        />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "Solutions", "item": "https://cetasol.com/solutions" },
            { "@type": "ListItem", "position": 3, "name": "CetaNav", "item": "https://cetasol.com/cetanav" },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "CetaNav",
          "description":
            "Fleet tracking and vessel monitoring using navigation data, including route monitoring, zone alerts and fuel consumption estimation without engine sensors.",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "provider": { "@type": "Organization", "name": "Cetasol" },
          "url": "https://cetasol.com/cetanav",
        })}</script>
      </Helmet>
      <Header />

      <main className="pb-28">
        {/* Hero */}
        <section className="relative min-h-[80vh] md:min-h-[85vh] flex items-end overflow-hidden mb-16 md:mb-28">
          <img
            src={cetanavHero.url}
            alt="Blue and white workboat under way in a harbour, the type of vessel CetaNav tracks"
            className="absolute inset-0 w-full h-full object-cover object-center brightness-[0.82] saturate-[1.1] contrast-[1.05]"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent md:bg-gradient-to-r md:from-background md:via-background/80 md:to-background/20" />
          <div className="relative z-10 container mx-auto px-6 pb-12 pt-28 md:pb-16 md:pt-32">
            <motion.div
              initial={isMobile ? false : { opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: isMobile ? 0 : 0.7 }}
              className="max-w-2xl"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">
                Fleet tracking data · No sensors
              </p>
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-primary-foreground mb-3 leading-tight">
                CetaNav
              </h1>
              <p className="text-base sm:text-lg text-primary-foreground font-medium mb-4">
                Know where your boats are and how they're running
              </p>
              <p className="text-base sm:text-lg text-primary-foreground/80 leading-relaxed max-w-xl mb-8">
                Fleet visibility and operational insights without requiring engine sensors. CetaNav uses navigation
                data to give operators a clearer picture of vessel movement, routes and estimated fuel consumption.
              </p>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
                <Link
                  to="/book-a-demo"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-[#14699B] px-6 py-3 rounded-md font-medium text-sm transition-colors"
                >
                  Book a demo <ArrowRight size={16} />
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById("how-it-works");
                    if (el) smoothScrollTo(el.getBoundingClientRect().top + window.scrollY - 80, 800);
                  }}
                  className="inline-flex items-center gap-2 border border-primary-foreground/40 text-primary-foreground bg-secondary/18 px-6 py-3 rounded-md font-medium text-sm hover:bg-secondary/25 transition-colors"
                >
                  See how it works
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* The problem */}
        <section className="relative mb-16 md:mb-28 overflow-hidden">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
              <motion.div {...anim()} className="py-12 md:py-24">
                <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">The problem</p>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Older vessels, almost no digital footprint
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Picture a fleet of older mechanical-engine vessels with limited sensors and no digital visibility.
                  The vessels still work every day, but the answers you need are not written down anywhere.
                </p>
                <ul className="space-y-3">
                  {questions.map((q) => (
                    <li key={q} className="flex items-start gap-3 text-muted-foreground">
                      <span className="mt-2 h-2 w-2 rounded-full bg-primary shrink-0" />
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-foreground font-medium mt-6">
                  CetaNav creates that visibility using navigation data alone.
                </p>
              </motion.div>
              <div className="md:hidden">
                <img
                  src={cetanavFleet.url}
                  alt="Illustrated fleet map showing tracked vessel positions across ocean regions"
                  className="w-full"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          <img
            src={cetanavFleet.url}
            alt="Illustrated fleet map showing tracked vessel positions across ocean regions"
            className="hidden md:block absolute top-0 right-0 h-full w-1/2 object-cover object-center"
            loading="lazy"
          />
        </section>


        {/* What you get */}
        <section className="bg-surface-light py-16 md:py-24 mb-16 md:mb-28">
          <div className="container mx-auto px-6">
            <motion.div {...anim()} className="text-center mb-12">
              <p className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">What you get</p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground">
                Vessel tracking onboard and ashore
              </h2>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {features.map((f, i) => (
                <motion.div key={f.title} {...anim(i * 0.06)}>
                  <span className="block w-8 h-px bg-primary mb-3" />
                  <h3 className="font-display text-base font-bold text-foreground mb-1">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </motion.div>
              ))}
            </div>

          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="container mx-auto px-6 mb-16 md:mb-28 scroll-mt-24">
          <motion.div {...anim()} className="max-w-3xl mb-12">
            <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">How CetaNav works</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              Navigation signals in. Vessel tracking out.
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              CetaNav connects to the vessel through the Cetasol Main Unit (CMU) and uses GPS and navigation signals to
              track position, speed and movement. The information is sent to the cloud and becomes available both
              onboard and to shore-based teams, with no modifications to the vessel's engine or fuel systems.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-border">
            {flow.map((step, i) => (
              <motion.div key={step.title} {...anim(i * 0.08)} className="bg-background p-6 md:p-8 relative">
                <span className="font-display text-sm font-bold text-primary">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display text-base font-bold text-foreground mt-4 mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Built for fleet visibility */}
        <section className="bg-surface-light py-16 md:py-24 mb-16 md:mb-28">
          <div className="container mx-auto px-6">
            <motion.div {...anim()} className="max-w-3xl mb-12">
              <p className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">
                Built for fleet visibility
              </p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">
                What operators can see and act on
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                A longer route than usual, a vessel idling where it shouldn't, a boat moving at night with no booking.
                CetaNav surfaces the everyday details and the exceptions, privately to your team.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-8">
              {visibility.map((item, i) => (
                <motion.div
                  key={item}
                  {...anim(i * 0.04)}
                  className="border-l border-border pl-4"
                >
                  <span className="text-sm text-foreground leading-relaxed">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Starting point */}
        <section className="container mx-auto px-6 mb-16 md:mb-28">
          <motion.div {...anim()} className="max-w-3xl mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              CetaNav is your starting point
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Start with vessel oversight and expand as your needs grow. Digital bridge integration and accurate fuel
              reading with CetaFuel build on the same installation, so nothing already onboard has to be replaced.
            </p>
          </motion.div>

          <div className="flex flex-col md:flex-row md:items-stretch gap-4">
            {growth.map((step, i) => (
              <motion.div key={step.name} {...anim(i * 0.08)} className="flex-1 flex items-center gap-4">
                <Link
                  to={step.to}
                  className="flex-1 border border-border p-6 md:p-8 hover:border-primary/50 transition-colors"
                >
                  <span className="font-display text-xl font-bold text-foreground block mb-1">{step.name}</span>
                  <span className="text-sm text-muted-foreground">{step.sub}</span>
                </Link>
                {i < growth.length - 1 && (
                  <ArrowRight size={18} className="text-primary shrink-0 rotate-90 md:rotate-0" />
                )}
              </motion.div>
            ))}
          </div>
          <motion.p {...anim(0.2)} className="text-sm text-muted-foreground mt-6">
            Satellite tracking integrations can be added where relevant.
          </motion.p>
        </section>

        {/* Final CTA */}
        <section className="container mx-auto px-6">
          <motion.div
            {...anim()}
            className="max-w-5xl mx-auto border border-border p-8 md:p-12 text-center"
          >
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">
              Ready to get visibility over your vessels?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              See how CetaNav can bring fleet visibility and operational insights to vessels without requiring complex
              engine integrations.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/book-a-demo"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-[#14699B] px-8 py-3.5 rounded-md font-medium text-sm transition-colors"
              >
                Book a free demo <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-[hsl(var(--secondary-border))] text-foreground bg-secondary/18 px-8 py-3.5 rounded-md font-medium text-sm hover:bg-secondary/25 transition-colors"
              >
                Contact us
              </Link>
            </div>
          </motion.div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CetaNav;
